import { REVIEW_BASE_PATH } from './constants';
import { DEFAULT_REVIEW_BASES, FALLBACK_SCENARIO, REVIEW_BASIS_CATEGORY_ORDER } from './data';
import { buildHref } from '@shared/utils';
import getItemPathByItemType from '@modules/documents/helpers/getItemPathByItemType.helper';
import {
  TFindingDocument,
  TFindingState,
  TFindingStatus,
  TReview,
  TReviewBasis,
  TReviewHistoryEntry,
  TReviewMockConfig,
  TReviewRegisterEntry,
  TReviewScenario,
  TReviewSeverity,
  TReviewSpaceConfig,
} from './types';

// Visibility only needs the flag, so the nav can gate on the raw organization
// config without depending on the full scenario types.
export const isReviewMockVisible = (config?: { enabled?: boolean }): boolean => Boolean(config?.enabled);

// The query keeps the register, breadcrumbs and navigation in the product's
// TD context; without a product the register belongs to QM.
const withProduct = (path: string, productId?: string) => (productId ? `${path}?productId=${productId}` : path);

export const getReviewsPath = (productId?: string): string => withProduct(REVIEW_BASE_PATH, productId);
export const getReviewSetupPath = (productId?: string): string => withProduct(`${REVIEW_BASE_PATH}/new`, productId);
export const getReviewPath = (reviewId: string, productId?: string): string =>
  withProduct(`${REVIEW_BASE_PATH}/${reviewId}`, productId);
export const getFindingPath = (reviewId: string, findingId: string, productId?: string): string =>
  withProduct(`${REVIEW_BASE_PATH}/${reviewId}/findings/${findingId}`, productId);

export const getFindingDocumentType = (document: TFindingDocument): NonNullable<TFindingDocument['itemType']> =>
  document.itemType ?? (document.mimetype === 'application/pdf' || /\.pdf$/i.test(document.name) ? 'FILE' : 'DOCUMENT');

export const getFindingDocumentHref = (document: TFindingDocument): string | undefined => {
  if (!document.documentId) return undefined;

  return buildHref(
    `/${getItemPathByItemType(getFindingDocumentType(document))}/${encodeURIComponent(document.documentId)}`,
    {
      elem: document.deepLink?.elementId,
      highlightText: document.deepLink?.highlightText ?? document.highlightText ?? document.excerpt,
    }
  );
};

// Product spaces only see their own entry, so a product without one falls back
// to the generic scenario and an empty history.
export const getReviewSpace = (
  config: TReviewMockConfig | undefined,
  productId?: string
): TReviewSpaceConfig | undefined =>
  productId ? config?.products?.find((product) => product.productId === productId) : config?.qm;

export const getReviewBases = (config?: TReviewMockConfig): TReviewBasis[] =>
  config?.reviewBases?.length ? config.reviewBases : DEFAULT_REVIEW_BASES;

// Categories keep the configured order for anything outside the known set, so a
// new category in the config shows up without a code change.
export const getBasisCategories = (bases: TReviewBasis[]): string[] => {
  const categories = [...new Set(bases.map((basis) => basis.category))];
  return categories.sort((a, b) => {
    const indexA = REVIEW_BASIS_CATEGORY_ORDER.indexOf(a);
    const indexB = REVIEW_BASIS_CATEGORY_ORDER.indexOf(b);
    if (indexA === -1 && indexB === -1) return 0;
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });
};

export const getBasisLabel = (bases: TReviewBasis[], basisId: string): string =>
  bases.find((basis) => basis.id === basisId)?.label ?? basisId;

// A scenario is picked from the review's space by its trigger basis. Selecting
// further bases never prevents a match; when several scenarios match, the
// highest priority wins.
export const resolveScenario = (
  config: TReviewMockConfig | undefined,
  basisIds: string[],
  productId?: string
): TReviewScenario => {
  const matches = (getReviewSpace(config, productId)?.scenarios ?? []).filter(
    (scenario) => scenario.trigger?.reviewBasisId && basisIds.includes(scenario.trigger.reviewBasisId)
  );
  if (matches.length === 0) return FALLBACK_SCENARIO;
  return matches.reduce((best, scenario) =>
    (scenario.trigger?.priority ?? 0) > (best.trigger?.priority ?? 0) ? scenario : best
  );
};

export const isOpenStatus = (status: TFindingStatus): boolean => status === 'unresolved' || status === 'assigned';

export type TFindingSummary = {
  total: number;
  open: number;
  resolved: number;
  dismissed: number;
  bySeverity: Record<TReviewSeverity, number>;
};

// Severity counts are open findings only, so the summary reacts as findings get
// resolved during a review.
export const getFindingSummary = (findings: TFindingState[]): TFindingSummary => {
  const summary: TFindingSummary = {
    total: findings.length,
    open: 0,
    resolved: 0,
    dismissed: 0,
    bySeverity: { major: 0, minor: 0, needsReview: 0 },
  };
  findings.forEach((finding) => {
    if (finding.status === 'resolved') summary.resolved += 1;
    else if (finding.status === 'dismissed') summary.dismissed += 1;
    else {
      summary.open += 1;
      summary.bySeverity[finding.severity] += 1;
    }
  });
  return summary;
};

export const getCategories = (findings: TFindingState[]): string[] => [
  ...new Set(findings.map((finding) => finding.category).filter(Boolean)),
];

export const getFindingDocumentNames = (findings: TFindingState[]): string[] => [
  ...new Set(findings.flatMap((finding) => (finding.affectedDocuments ?? []).map((document) => document.name))),
];

export const formatReviewDate = (value?: number | string): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

export const getReviewCompletedAt = (review: { startedAt: number; durationMs: number }): number =>
  review.startedAt + review.durationMs;

export const isReviewRunning = (review: { startedAt: number; durationMs: number }, now: number): boolean =>
  now < getReviewCompletedAt(review);

export const getReviewRegisterEntries = (
  reviews: TReview[],
  now: number,
  productId?: string,
  history: TReviewHistoryEntry[] = []
): TReviewRegisterEntry[] => {
  const created: TReviewRegisterEntry[] = reviews
    .filter((review) => (productId ? review.productId === productId : !review.productId))
    .map((review) => ({
      id: review.id,
      name: review.name,
      reviewBasisIds: review.reviewBasisIds,
      scopeLabel: review.scopeLabel,
      productName: review.productName,
      startedAt: review.startedAt,
      startedBy: review.startedBy,
      status: isReviewRunning(review, now) ? 'running' : 'completed',
      access: 'available',
      summary: getFindingSummary(review.findings),
    }));
  const earlier: TReviewRegisterEntry[] = history.map((entry) => ({
    id: entry.id,
    name: entry.name,
    reviewBasisIds: entry.reviewBasisIds,
    scopeLabel: entry.scopeLabel,
    startedAt: new Date(entry.startedAt).getTime(),
    startedBy: entry.startedBy,
    status: 'completed',
    access: 'restricted',
    summary: {
      total: Object.values(entry.openFindings).reduce((sum, count) => sum + count, 0) + entry.handledFindings,
      bySeverity: entry.openFindings,
    },
  }));
  return [...created, ...earlier].sort((a, b) => b.startedAt - a.startedAt);
};

// Progress of a running review as a value between 0 and 1.
export const getReviewProgress = (review: { startedAt: number; durationMs: number }, now: number): number => {
  if (review.durationMs <= 0) return 1;
  return Math.min(1, Math.max(0, (now - review.startedAt) / review.durationMs));
};

export const getReviewStage = (messages: string[], progress: number): number => {
  if (messages.length === 0) return 0;
  return Math.min(messages.length - 1, Math.floor(progress * messages.length));
};
